var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
import { useContext } from 'react';
import { find } from 'lodash';
import { all, spread } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import { baseInstance as base, sessionInstance as session, baseURL, adminInstance as admin, publicInstance as pub, fullAuthInstance as full } from '../helpers/api';
import { setAccessToken, removeAccessToken } from '../helpers/token';
import { decodeCBOR, encodeCBOR } from '../helpers/cbor';
import { SessionContext } from '../contexts/session';
var _login = function _login(p) {
  return base.post('/login', _extends({}, p, {
    grant_type: 'password'
  }));
};
var _loginTfa = function _loginTfa(p) {
  return base.post('/login_tfa', p);
};
var _loginAzureAd = function _loginAzureAd(p) {
  return base.post('/login_azure_ad', p);
};
var _registerAuthDevice = function _registerAuthDevice(p) {
  var ftsData = void 0;
  var payload = {
    method: 'POST',
    headers: {
      'Content-Type': 'text/html'
    },
    body: JSON.stringify(p)
  };
  return fetch(baseURL + '/start-auth-device-registration', payload).then(function (r) {
    return r.arrayBuffer();
  }).then(function (rbody) {
    return decodeCBOR(rbody);
  }).then(function (options) {
    ftsData = options.FTSData;
    return navigator.credentials.create({
      publicKey: options.publicKey
    });
  }).then(function (creds) {
    var credPayload = {
      method: 'POST',
      headers: {
        'Content-Type': 'text/html'
      },
      body: encodeCBOR(_extends({}, p, {
        FTSData: ftsData,
        attestationObject: new Uint8Array(creds.response.attestationObject),
        clientDataJSON: new Uint8Array(creds.response.clientDataJSON)
      }))
    };
    return fetch(baseURL + '/complete-auth-device-registration', credPayload);
  });
};
var _loginWithDevice = function _loginWithDevice(p) {
  var ftsData = void 0;
  var payload = {
    method: 'POST',
    headers: {
      'Content-Type': 'text/html'
    },
    body: JSON.stringify(p)
  };
  return fetch(baseURL + '/start-device-auth', payload).then(function (r) {
    return r.arrayBuffer();
  }).then(function (rbody) {
    return decodeCBOR(rbody);
  }).then(function (options) {
    ftsData = options.FTSData;
    return navigator.credentials.get({
      publicKey: options.publicKey
    });
  }).then(function (assertion) {
    var assertionPayload = {
      method: 'POST',
      headers: {
        'Content-Type': 'text/html'
      },
      body: encodeCBOR(_extends({}, p, {
        FTSData: ftsData,
        credentialId: new Uint8Array(assertion.rawId),
        authenticatorData: new Uint8Array(assertion.response.authenticatorData),
        clientDataJSON: new Uint8Array(assertion.response.clientDataJSON),
        signature: new Uint8Array(assertion.response.signature)
      }))
    };
    return fetch(baseURL + '/complete-device-auth', assertionPayload);
  }).then(function (response) {
    return response.json();
  });
};
var _changePassword = function _changePassword(p) {
  return session.post('/change-password', p);
};
var _logout = function _logout() {
  return session.post('/logout');
};
var _fetchAppUser = function _fetchAppUser(sesh) {
  return (find(sesh.attributes.permissions, ['securityPermissionName', 'hasAppKickstarter']) ? admin : pub).get('/app-user/' + sesh.attributes.userUid).then(function (_ref) {
    var user = _ref.data.data;
    return all([user, full.get(user.relationships.organizationUsers.related)]);
  }).then(spread(function (user, orgUsers) {
    // eslint-disable-next-line
    user.relationships.organizationUsers = orgUsers.data.data;
    return user;
  }));
};
var useSession = function useSession() {
  var _useContext = useContext(SessionContext),
    current = _useContext.current,
    setCurrent = _useContext.setCurrent,
    appUser = _useContext.appUser,
    setAppUser = _useContext.setAppUser,
    isAuthed = _useContext.isAuthed,
    setIsAuthed = _useContext.setIsAuthed,
    debugMode = _useContext.debugMode,
    toggleDebugMode = _useContext.toggleDebugMode;
  var afterLogin = function afterLogin(r) {
    setIsAuthed(!!r.attributes.accessToken);
    if (r.attributes.accessToken) {
      setAccessToken(r.attributes.accessToken);
      setCurrent(r);
    }
  };
  var afterLogout = function afterLogout() {
    setIsAuthed(false);
    setAppUser(null);
    setCurrent(null);
    removeAccessToken();
  };
  return {
    isAuthed: isAuthed,
    current: current,
    currentAppUser: appUser,
    debugMode: debugMode,
    toggleDebugMode: toggleDebugMode,
    refresh: function refresh() {
      return session.get('/current');
    },
    fetchAppUser: function fetchAppUser(p) {
      return _fetchAppUser(p).then(setAppUser);
    },
    login: function login(p) {
      return _login(p).then(function (_ref2) {
        var r = _ref2.data.data;
        afterLogin(r);
        return r;
      });
    },
    loginTfa: function loginTfa(p) {
      return _loginTfa(p).then(function (_ref3) {
        var r = _ref3.data.data;
        afterLogin(r);
        return r;
      });
    },
    loginAzureAd: function loginAzureAd(p) {
      return _loginAzureAd(p).then(function (_ref4) {
        var r = _ref4.data.data;
        afterLogin(r);
        return r;
      });
    },
    registerAuthDevice: function registerAuthDevice(p) {
      return _registerAuthDevice(p);
    },
    loginWithDevice: function loginWithDevice(p) {
      return _loginWithDevice(p).then(function (r) {
        var d = r.data;
        var data = camelcaseKeys(d, {
          deep: true
        });
        afterLogin(data);
        return data;
      });
    },
    changePassword: function changePassword(p) {
      return _changePassword(p).then(function (_ref5) {
        var r = _ref5.data.data;
        setAccessToken(r.attributes.accessToken);
        setCurrent(r);
        return r;
      });
    },
    logout: function logout() {
      return _logout().then(afterLogout).catch(afterLogout);
    }
  };
};
export default useSession;